import React, { createContext, useContext, useState, useEffect } from 'react';
import { useUser, useClerk } from '@clerk/clerk-react';

const NavContext = createContext();

export const NavProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Get Clerk user data and signOut function
  const { user, isLoaded: clerkLoaded, isSignedIn } = useUser();
  const { signOut } = useClerk(); // Use useClerk directly

  useEffect(() => {
    const initializeAuth = () => {
      // Check localStorage for auth info first
      const storedUserInfo = localStorage.getItem('userInfo');
      const storedToken = localStorage.getItem('userToken');

      if (storedUserInfo && storedToken) {
        try {
          const userData = JSON.parse(storedUserInfo);
          setIsAuthenticated(true);
          setUserInfo(userData);
          setIsLoading(false);
          return;
        } catch (error) {
          console.error('Error parsing stored user info:', error);
          // Clear invalid data
          localStorage.removeItem('userInfo');
          localStorage.removeItem('userToken');
          localStorage.removeItem('userName');
        }
      }

      // If Clerk is loaded, check Clerk auth state
      if (clerkLoaded) {
        if (isSignedIn && user) {
          const firstName = user.firstName || '';
          const lastName = user.lastName || '';
          const fullName = `${firstName} ${lastName}`.trim();

          const userData = {
            name: fullName || user.primaryEmailAddress?.emailAddress || 'User',
            email: user.primaryEmailAddress?.emailAddress || '',
            isLoggedIn: true,
            profileImage: user.imageUrl,
            provider: user.externalAccounts.length > 0
              ? user.externalAccounts[0].provider.toUpperCase()
              : 'EMAIL',
          };

          setIsAuthenticated(true);
          setUserInfo(userData);

          // Sync with localStorage
          localStorage.setItem('userInfo', JSON.stringify(userData));
        } else {
          // Only set to false if there's no stored auth
          if (!storedUserInfo || !storedToken) {
            setIsAuthenticated(false);
            setUserInfo(null);
          }
        }
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, [clerkLoaded, isSignedIn, user]);

  // Additional effect to handle Clerk authentication changes
  useEffect(() => {
    if (clerkLoaded && isSignedIn && user && !isAuthenticated) {
      const firstName = user.firstName || '';
      const lastName = user.lastName || '';
      const fullName = `${firstName} ${lastName}`.trim();

      const userData = {
        name: fullName || user.primaryEmailAddress?.emailAddress || 'User',
        email: user.primaryEmailAddress?.emailAddress || '',
        isLoggedIn: true,
        profileImage: user.imageUrl,
        provider: user.externalAccounts.length > 0
          ? user.externalAccounts[0].provider.toUpperCase()
          : 'EMAIL',
      };

      login(userData);
    }
  }, [clerkLoaded, isSignedIn, user, isAuthenticated]);

  const login = (userData) => {
    setIsAuthenticated(true);
    setUserInfo(userData);

    // Store in localStorage
    localStorage.setItem('userInfo', JSON.stringify(userData));
    if (userData.token) {
      localStorage.setItem('userToken', userData.token);
    }
    if (userData.name) {
      localStorage.setItem('userName', userData.name);
    }
  };

  const logout = async () => {
    setIsAuthenticated(false);
    setUserInfo(null);

    // Clear localStorage
    localStorage.removeItem('userToken');
    localStorage.removeItem('userName');
    localStorage.removeItem('userInfo');

    // Sign out from Clerk if signed in
    if (isSignedIn) {
      try {
        await signOut();
      } catch (error) {
        console.error('Error signing out from Clerk:', error);
      }
    }
  };

  const value = {
    isAuthenticated,
    userInfo,
    isLoading,
    login,
    logout,
  };

  return <NavContext.Provider value={value}>{children}</NavContext.Provider>;
};

export const useNav = () => {
  const context = useContext(NavContext);
  if (!context) {
    throw new Error('useNav must be used within a NavProvider');
  }
  return context;
};